import { memo } from "react";
import { Link } from "react-router-dom";
import { Phone, Clock, AlertTriangle } from "lucide-react";
import { Button } from "./ui/button";
import { useAnalytics } from "@/hooks/useAnalytics";
import BrandBolt from "./BrandBolt";

const EmergencyBanner = memo(() => {
  const { trackPhoneClick, trackEmergencyClick } = useAnalytics();
  return (
    <section 
      className="py-12 bg-gradient-to-r from-red-600 via-red-500 to-orange-500 relative overflow-hidden"
      aria-label="Pohotovostní služba 24/7"
    >
      {/* Static background elements - no animations */}
      <div className="absolute inset-0 opacity-20 hidden md:block" aria-hidden="true">
        <div className="absolute top-0 left-1/4 w-32 h-32 bg-white rounded-full blur-3xl" />
        <div className="absolute bottom-0 right-1/4 w-48 h-48 bg-yellow-300 rounded-full blur-3xl" />
      </div>
      
      <div className="container mx-auto px-4 relative z-10">
        <div className="flex flex-col lg:flex-row items-center justify-between gap-8">
          {/* Left side - Icon and Title */}
          <div className="flex items-center gap-6 text-white">
            <div className="relative" aria-hidden="true">
              <div className="w-20 h-20 rounded-2xl bg-white/20 backdrop-blur-sm flex items-center justify-center border border-white/30">
                <BrandBolt size="xl" variant="pulse" />
              </div>
              <div className="absolute -top-2 -right-2 w-8 h-8 rounded-full bg-yellow-400 flex items-center justify-center">
                <AlertTriangle className="h-4 w-4 text-red-600" />
              </div>
            </div>
            
            <div>
              <div className="flex items-center gap-3 mb-2">
                <Clock className="h-5 w-5" aria-hidden="true" />
                <span className="text-sm font-semibold uppercase tracking-wider text-white/90">
                  Nonstop služba
                </span>
              </div>
              <h2 className="text-3xl md:text-4xl font-black">
                POHOTOVOST 24/7
              </h2>
              <p className="text-white/80 mt-1 text-lg">
                Havarijní elektrikář kdykoliv vás potřebujete • <span className="font-semibold">Doprava 25 Kč/km</span>
              </p>
            </div>
          </div>
          
          {/* Right side - CTA */}
          <div className="flex flex-col sm:flex-row items-center gap-4">
            <a 
              href="tel:+420732658712" 
              aria-label="Zavolat pohotovost na číslo +420 732 658 712"
              onClick={() => {
                trackEmergencyClick();
                trackPhoneClick('+420732658712');
              }}
            >
              <Button 
                size="xl" 
                className="bg-white text-red-600 hover:bg-yellow-400 hover:text-slate-900 gap-3 font-bold shadow-xl shadow-black/20"
              >
                <Phone className="h-6 w-6" aria-hidden="true" />
                <span className="text-lg">+420 732 658 712</span>
              </Button>
            </a>
            
            <Link 
              to="/pohotovost" 
              aria-label="Zobrazit ceník pohotovostních služeb"
              onClick={trackEmergencyClick}
            >
              <Button 
                variant="outline" 
                size="lg"
                className="border-2 border-white bg-white/10 text-white hover:bg-white hover:text-red-600 font-semibold"
              >
                Zobrazit ceník
              </Button>
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
});

EmergencyBanner.displayName = "EmergencyBanner";

export default EmergencyBanner;
